// src/components/admin/AdminSugerirCandidatos.js
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import "./AdminSugerirCandidatos.css";
import {
  collection, query, where, orderBy, limit, startAfter, getDocs,
  doc, getDoc, setDoc, updateDoc, deleteDoc, serverTimestamp
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

// Config (mesmo doc usado no front do modal)
const CONFIG_DOC_PATH = ["config", "sugerirCandidatos"];
const PAGE_SIZE = 10;

// UID permitido nas rules atuais (confere em firestore.rules -> isAdmin())
const RULES_ADMIN_UID = "CwveE0ysF6agpZiWmG4beKqokNB3";

const PREMIACOES = [
  ["oscar", "Oscar"],
  ["globodeouro", "Globo de Ouro"],
  ["bafta", "BAFTA"],
  ["criticschoice", "Critics Choice"],
  ["sagawards", "SAG Awards"],
  ["pga", "PGA"],
  ["dga", "DGA"],
];

function useOnce(fn, deps) {
  const [ran, setRan] = useState(false);
  useEffect(() => {
    if (!ran) {
      setRan(true);
      fn?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default function AdminSugerirCandidatos() {
  console.log("[AdminSugerirCandidatos] mount");

  // Quem está logado agora?
  const [meUid, setMeUid] = useState(null);
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setMeUid(u?.uid || null);
      console.log("[AdminSC] auth state -> uid:", u?.uid);
    });
    return () => unsub();
  }, []);
  const amIAdminByRules = meUid === RULES_ADMIN_UID;

  // ctrl da aba interna do admin: pendentes / em_votacao / aprovados
  const [subtab, setSubtab] = useState("pendentes");

  // CONFIG
  const [cfgLoading, setCfgLoading] = useState(false);
  const [cfg, setCfg] = useState(null);

  // LISTAS
  const [pendentes, setPendentes] = useState([]);
  const [pendLoading, setPendLoading] = useState(false);
  const [pendHasMore, setPendHasMore] = useState(false);
  const [pendCursor, setPendCursor] = useState(null);

  const [votacao, setVotacao] = useState([]);
  const [votaLoading, setVotaLoading] = useState(false);
  const [votaHasMore, setVotaHasMore] = useState(false);
  const [votaCursor, setVotaCursor] = useState(null);

  const [aprovados, setAprovados] = useState([]);
  const [aprovLoading, setAprovLoading] = useState(false);
  const [aprovHasMore, setAprovHasMore] = useState(false);
  const [aprovCursor, setAprovCursor] = useState(null);

  // Painéis inline
  const [editItem, setEditItem] = useState(null);  // {id,titulo,filme,pais,imagemUrl}
  const [votosItem, setVotosItem] = useState(null);
  const [votosVal, setVotosVal] = useState(40);

  // Banner de mensagens
  const [banner, setBanner] = useState({ type: "", text: "" }); // type: "ok" | "err" | ""

  const showErr = (msg) => setBanner({ type: "err", text: msg });
  const showOk  = (msg) => setBanner({ type: "ok", text: msg });
  const clearBanner = () => setBanner({ type: "", text: "" });

  // ====== CONFIG ======
  async function loadConfig() {
    setCfgLoading(true);
    console.log("[AdminSC] loadConfig:start");
    try {
      const ref = doc(db, ...CONFIG_DOC_PATH);
      const snap = await getDoc(ref);
      console.log("[AdminSC] loadConfig:snap.exists:", snap.exists());
      if (snap.exists()) {
        const data = snap.data();
        console.log("[AdminSC] loadConfig:data:", data);
        setCfg(data);
      } else {
        const defaultCfg = {
          enabled: false,
          votosPadrao: 40,
          premiacoesAtivas: {
            oscar: true,
            globodeouro: true,
            bafta: true,
            criticschoice: true,
            pga: true,
            dga: true,
          },
          updatedAt: serverTimestamp(),
        };
        console.log("[AdminSC] loadConfig:creating default config");
        await setDoc(ref, defaultCfg, { merge: true });
        setCfg(defaultCfg);
      }
    } catch (e) {
      console.error("[AdminSC] loadConfig:error", e);
      showErr(`Não foi possível carregar a configuração.\n${e.code || ""} ${e.message || ""}`);
    } finally {
      setCfgLoading(false);
      console.log("[AdminSC] loadConfig:end");
    }
  }

  async function saveConfig(next) {
    console.log("[AdminSC] saveConfig:next =", next);
    try {
      const ref = doc(db, ...CONFIG_DOC_PATH);
      await setDoc(ref, { ...next, updatedAt: serverTimestamp() }, { merge: true });
      setCfg((c) => ({ ...(c || {}), ...next }));
      console.log("[AdminSC] saveConfig:ok");
      showOk("Configurações salvas.");
    } catch (e) {
      console.error("[AdminSC] saveConfig:error", e);
      showErr(`Não foi possível salvar a configuração.\n${e.code || ""} ${e.message || ""}`);
    }
  }

  useOnce(loadConfig, []);

  // ====== LISTAS (carregar só quando abrir a subtab) ======
  async function loadPendentes(initial = true) {
    if (pendLoading) return;
    setPendLoading(true);
    console.log("[AdminSC] loadPendentes:start initial=", initial, "cursor=", !!pendCursor);
    try {
      const colr = collection(db, "sugestoes");
      const base = [
        where("status", "==", "pendente"),
        orderBy("createdAt", "desc"),
        limit(PAGE_SIZE),
      ];
      const qy = initial || !pendCursor ? query(colr, ...base) : query(colr, ...base, startAfter(pendCursor));
      const snap = await getDocs(qy);
      console.log("[AdminSC] loadPendentes:snap size=", snap.size);
      const docs = snap.docs || [];
      const items = docs.map(d => ({ id: d.id, ...d.data() }));
      console.log("[AdminSC] loadPendentes:items=", items);
      setPendentes((cur) => (initial ? items : [...cur, ...items]));
      if (docs.length === PAGE_SIZE) {
        setPendHasMore(true);
        setPendCursor(docs[docs.length - 1]);
      } else { setPendHasMore(false); setPendCursor(null); }
    } catch (e) {
      console.error("[AdminSC] loadPendentes:error", e);
      showErr(`Falha ao carregar pendentes.\n${e.code || ""} ${e.message || ""}`);
    } finally {
      setPendLoading(false);
      console.log("[AdminSC] loadPendentes:end");
    }
  }

  async function loadVotacao(initial = true) {
    if (votaLoading) return;
    setVotaLoading(true);
    console.log("[AdminSC] loadVotacao:start initial=", initial, "cursor=", !!votaCursor);
    try {
      const colr = collection(db, "sugestoes");
      const base = [
        where("status", "==", "em_votacao"),
        orderBy("votosCount", "desc"),
        orderBy("createdAt", "asc"),
        limit(PAGE_SIZE),
      ];
      const qy = initial || !votaCursor ? query(colr, ...base) : query(colr, ...base, startAfter(votaCursor));
      const snap = await getDocs(qy);
      console.log("[AdminSC] loadVotacao:snap size=", snap.size);
      const docs = snap.docs || [];
      const items = docs.map(d => ({ id: d.id, ...d.data() }));
      console.log("[AdminSC] loadVotacao:items=", items);
      setVotacao((cur) => (initial ? items : [...cur, ...items]));
      if (docs.length === PAGE_SIZE) {
        setVotaHasMore(true);
        setVotaCursor(docs[docs.length - 1]);
      } else { setVotaHasMore(false); setVotaCursor(null); }
    } catch (e) {
      console.error("[AdminSC] loadVotacao:error", e);
      showErr(`Falha ao carregar votação.\n${e.code || ""} ${e.message || ""}`);
    } finally {
      setVotaLoading(false);
      console.log("[AdminSC] loadVotacao:end");
    }
  }

  async function loadAprovados(initial = true) {
    if (aprovLoading) return;
    setAprovLoading(true);
    console.log("[AdminSC] loadAprovados:start initial=", initial, "cursor=", !!aprovCursor);
    try {
      const colr = collection(db, "sugestoes");
      const base = [
        where("status", "==", "aprovado"),
        orderBy("aprovadoAt", "desc"),
        limit(PAGE_SIZE),
      ];
      const qy = initial || !aprovCursor ? query(colr, ...base) : query(colr, ...base, startAfter(aprovCursor));
      const snap = await getDocs(qy);
      console.log("[AdminSC] loadAprovados:snap size=", snap.size);
      const docs = snap.docs || [];
      const items = docs.map(d => ({ id: d.id, ...d.data() }));
      console.log("[AdminSC] loadAprovados:items=", items);
      setAprovados((cur) => (initial ? items : [...cur, ...items]));
      if (docs.length === PAGE_SIZE) {
        setAprovHasMore(true);
        setAprovCursor(docs[docs.length - 1]);
      } else { setAprovHasMore(false); setAprovCursor(null); }
    } catch (e) {
      console.error("[AdminSC] loadAprovados:error", e);
      showErr(`Falha ao carregar aprovados.\n${e.code || ""} ${e.message || ""}`);
    } finally {
      setAprovLoading(false);
      console.log("[AdminSC] loadAprovados:end");
    }
  }

  useEffect(() => {
    console.log("[AdminSC] subtab changed ->", subtab);
    if (subtab === "pendentes" && pendentes.length === 0) loadPendentes(true);
    if (subtab === "votacao" && votacao.length === 0) loadVotacao(true);
    if (subtab === "aprovados" && aprovados.length === 0) loadAprovados(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtab]);

  // ====== AÇÕES ======
  async function rejeitar(item) {
    console.log("[AdminSC] click: rejeitar", item?.id, item);
    if (!item?.id) return;
    try {
      await deleteDoc(doc(db, "sugestoes", item.id));
      console.log("[AdminSC] rejeitar:ok", item.id);
      setPendentes((cur) => (Array.isArray(cur) ? cur.filter(x => x.id !== item.id) : []));
      setVotacao((cur) => (Array.isArray(cur) ? cur.filter(x => x.id !== item.id) : []));
      setAprovados((cur) => (Array.isArray(cur) ? cur.filter(x => x.id !== item.id) : []));
      showOk("Sugestão apagada.");
    } catch (e) {
      console.error("[AdminSC] rejeitar:error", e);
      showErr(`Não foi possível rejeitar/apagar.\n${e.code || ""} ${e.message || ""}`);
    }
  }

  async function aprovarParaVotacao(item) {
    console.log("[AdminSC] click: aprovarParaVotacao", item?.id, item);
    if (!item?.id) return;
    try {
      await updateDoc(doc(db, "sugestoes", item.id), {
        status: "em_votacao",
        votosCount: item.votosCount || 0,
        votosNecessarios: item.votosNecessarios || (cfg?.votosPadrao || 40),
        updatedAt: serverTimestamp(),
      });
      console.log("[AdminSC] aprovarParaVotacao:ok", item.id);
      setPendentes((cur) => (Array.isArray(cur) ? cur.filter(x => x.id !== item.id) : []));
      setVotacao((cur) => [{ ...item, status: "em_votacao" }, ...(Array.isArray(cur) ? cur : [])]);
      showOk("Movido para 'Em votação'.");
    } catch (e) {
      console.error("[AdminSC] aprovarParaVotacao:error", e);
      showErr(`Não foi possível aprovar para votação.\n${e.code || ""} ${e.message || ""}`);
    }
  }

  async function aprovarParaAprovados(item) {
    console.log("[AdminSC] click: aprovarParaAprovados", item?.id, item);
    if (!item?.id) return;
    const aprovadoPor = item.especialistaAutor ? "especialistaAutor" : "votos";
    try {
      await updateDoc(doc(db, "sugestoes", item.id), {
        status: "aprovado",
        aprovadoPor,
        aprovadoAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      console.log("[AdminSC] aprovarParaAprovados:ok", item.id, "aprovadoPor=", aprovadoPor);
      setPendentes((cur) => (Array.isArray(cur) ? cur.filter(x => x.id !== item.id) : []));
      setVotacao((cur) => (Array.isArray(cur) ? cur.filter(x => x.id !== item.id) : []));
      setAprovados((cur) => [{ ...item, status: "aprovado", aprovadoPor }, ...(Array.isArray(cur) ? cur : [])]);
      showOk("Aprovado.");
    } catch (e) {
      console.error("[AdminSC] aprovarParaAprovados:error", e);
      showErr(`Não foi possível aprovar para aprovados.\n${e.code || ""} ${e.message || ""}`);
    }
  }

  // EDITAR (corrigir títulos e imagem) — painel inline
  function openEditar(item) {
    console.log("[AdminSC] click: openEditar", item?.id, item);
    setEditItem({
      id: item.id,
      titulo: item.titulo || "",
      filme: item.filme || "",
      pais: item.pais || "",
      imagemUrl: item.imagemUrl || "",
    });
    clearBanner();
  }

  async function salvarEdicao() {
    console.log("[AdminSC] click: salvarEdicao", editItem);
    if (!editItem?.id) return;
    try {
      const { id, titulo, filme, pais, imagemUrl } = editItem;
      await updateDoc(doc(db, "sugestoes", id), {
        titulo: titulo?.trim() || "",
        filme: filme?.trim() || "",
        pais: pais?.trim() || "",
        imagemUrl: imagemUrl?.trim() || "",
        updatedAt: serverTimestamp(),
      });
      console.log("[AdminSC] salvarEdicao:ok", id);
      const apply = (arr) => (Array.isArray(arr) ? arr.map(x => x.id === id ? { ...x, ...editItem } : x) : arr);
      setPendentes(apply);
      setVotacao(apply);
      setAprovados(apply);
      setEditItem(null);
      showOk("Alterações salvas.");
    } catch (e) {
      console.error("[AdminSC] salvarEdicao:error", e);
      showErr(`Não foi possível salvar a edição.\n${e.code || ""} ${e.message || ""}`);
    }
  }

  // VOTOS NECESSÁRIOS — painel inline
  function openVotos(item) {
    console.log("[AdminSC] click: openVotos", item?.id, item);
    setVotosItem(item);
    setVotosVal(Number(item?.votosNecessarios || cfg?.votosPadrao || 40));
    clearBanner();
  }

  async function salvarVotosNec() {
    console.log("[AdminSC] click: salvarVotosNec", votosItem?.id, "->", votosVal);
    if (!votosItem?.id) return;
    const val = Math.max(1, Number(votosVal || 1));
    try {
      await updateDoc(doc(db, "sugestoes", votosItem.id), {
        votosNecessarios: val,
        updatedAt: serverTimestamp(),
      });
      console.log("[AdminSC] salvarVotosNec:ok", votosItem.id, "novo =", val);
      const apply = (arr) => (Array.isArray(arr) ? arr.map(x => x.id === votosItem.id ? { ...x, votosNecessarios: val } : x) : arr);
      setPendentes(apply); setVotacao(apply); setAprovados(apply);
      setVotosItem(null);
      showOk("Votos necessários atualizados.");
    } catch (e) {
      console.error("[AdminSC] salvarVotosNec:error", e);
      showErr(`Não foi possível atualizar os votos necessários.\n${e.code || ""} ${e.message || ""}`);
    }
  }

  function LinhaSugestao({ it, contexto }) {
    console.log("[AdminSC] render LinhaSugestao:", contexto, it?.id, it);
    const onBtn = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn?.(); };

    return (
      <div className="admin-sc-row">
        <div className="c1">
          <div className="admin-sc-title">{it.titulo || "—"}</div>
          {it.filme && <div className="admin-sc-sub">{it.filme}</div>}
          {it.pais && !it.filme && <div className="admin-sc-sub">{it.pais}</div>}
          <div className="admin-sc-meta">
            <span>{(it.premiacaoId || "").toUpperCase()}</span>
            <span>•</span>
            <span>{(it.categoriaId || "").toUpperCase()}</span>
            {it?.sugeridoPor?.nome && <> <span>•</span> <span>Sugerido por: {it.sugeridoPor.nome}</span> </>}
            {it.especialistaAutor && <> <span>•</span> <span>Especialista</span> </>}
          </div>
        </div>
        <div className="c2">
          <div className="admin-sc-img">
            <img src={it.imagemUrl || "https://i.imgur.com/eOjYRf6.png"} alt="" />
          </div>
        </div>
        <div className="c3">
          {contexto === "votacao" && (
            <div className="admin-sc-votos">
              <div><strong>{it.votosCount ?? 0}</strong> / {it.votosNecessarios ?? (cfg?.votosPadrao || 40)}</div>
              <small>em votação</small>
            </div>
          )}
          {contexto === "aprovado" && (
            <div className="admin-sc-votos">
              <div><strong>{it.votosCount ?? 0}</strong> / {it.votosNecessarios ?? (cfg?.votosPadrao || 40)}</div>
              <small>aprovado por: {it.aprovadoPor || "—"}</small>
            </div>
          )}
        </div>
        <div className="c4">
          {contexto === "pendente" && (
            <>
              <button type="button" className="admin-sc-btn" onClick={onBtn(() => rejeitar(it))}>Rejeitar</button>
              <button type="button" className="admin-sc-btn" onClick={onBtn(() => aprovarParaVotacao(it))}>Aprovar para votação</button>
              {it.especialistaAutor && (
                <button type="button" className="admin-sc-btn" onClick={onBtn(() => aprovarParaAprovados(it))}>Aprovar para aprovados</button>
              )}
              <button type="button" className="admin-sc-btn" onClick={onBtn(() => openEditar(it))}>Editar</button>
              <button type="button" className="admin-sc-btn" onClick={onBtn(() => openVotos(it))}>Votos necessários</button>
              <button type="button" className="admin-sc-btn danger" onClick={onBtn(() => rejeitar(it))}>Apagar totalmente</button>
            </>
          )}

          {contexto === "votacao" && (
            <>
              <button type="button" className="admin-sc-btn" onClick={onBtn(() => rejeitar(it))}>Rejeitar</button>
              <button type="button" className="admin-sc-btn" onClick={onBtn(() => openEditar(it))}>Editar</button>
              <button type="button" className="admin-sc-btn" onClick={onBtn(() => openVotos(it))}>Votos necessários</button>
              <button type="button" className="admin-sc-btn danger" onClick={onBtn(() => rejeitar(it))}>Apagar totalmente</button>
            </>
          )}

          {contexto === "aprovado" && (
            <>
              <button type="button" className="admin-sc-btn" onClick={onBtn(() => openEditar(it))}>Editar</button>
              <button type="button" className="admin-sc-btn danger" onClick={onBtn(() => rejeitar(it))}>Apagar totalmente</button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-sc-wrap">
      <h2>Sugerir Candidatos</h2>

      {/* Badge de diagnóstico do UID/admin */}
      <div style={{margin:"6px 0 10px", fontSize:12}}>
        <div>UID atual: <code>{meUid || "—"}</code></div>
        {!amIAdminByRules ? (
          <div style={{color:"#ff6b6b", fontWeight:800}}>
            Você NÃO é admin nas rules. As ações (aprovar/rejeitar/editar) serão bloqueadas pelo Firestore.
          </div>
        ) : (
          <div style={{color:"#5ee47a", fontWeight:800}}>Admin reconhecido pelas rules.</div>
        )}
      </div>

      {/* Banner inline */}
      {banner.type && (
        <div
          style={{
            background: banner.type === "ok" ? "#1b5e20" : "#7b1b1b",
            border: "1px solid rgba(255,255,255,.08)",
            padding: "10px 12px",
            borderRadius: 10,
            marginBottom: 10,
            whiteSpace: "pre-line",
            fontWeight: 700
          }}
          role="status"
        >
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:12}}>
            <span>{banner.text}</span>
            <button type="button" className="admin-sc-btn" onClick={clearBanner}>Fechar</button>
          </div>
        </div>
      )}

      {/* ====== CONFIG HEADER ====== */}
      <section className="admin-sc-config">
        <div className="line">
          <label className="switch">
            <input
              type="checkbox"
              checked={!!cfg?.enabled}
              onChange={(e) => saveConfig({ enabled: !!e.target.checked })}
              disabled={cfgLoading}
            />
            <span className="lbl">Exibir botão “Sugerir Candidato” na Timeline</span>
          </label>
        </div>

        <div className="line">
          <div className="lbl">Premiações ativas no dropdown:</div>
          <div className="checks">
            {PREMIACOES.map(([id, nome]) => {
              const on = !!cfg?.premiacoesAtivas?.[id];
              return (
                <label key={id} className="chk">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => {
                      const next = { ...(cfg?.premiacoesAtivas || {}) , [id]: !!e.target.checked };
                      saveConfig({ premiacoesAtivas: next });
                    }}
                    disabled={cfgLoading}
                  />
                  <span>{nome}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="line">
          <label className="lbl">Votos necessários padrão:</label>
          <input
            type="number"
            min={1}
            value={cfg?.votosPadrao ?? 40}
            onChange={(e) => {
              const v = Math.max(1, Number(e.target.value || 1));
              setCfg((c) => ({ ...(c||{}), votosPadrao: v }));
            }}
            onBlur={() => saveConfig({ votosPadrao: cfg?.votosPadrao ?? 40 })}
            className="num"
          />
        </div>
      </section>

      {/* ====== SUBTABS ====== */}
      <div className="admin-sc-tabs">
        <button type="button" className={`t ${subtab==="pendentes"?"on":""}`} onClick={() => setSubtab("pendentes")}>Pendentes</button>
        <button type="button" className={`t ${subtab==="votacao"?"on":""}`} onClick={() => setSubtab("votacao")}>Em votação</button>
        <button type="button" className={`t ${subtab==="aprovados"?"on":""}`} onClick={() => setSubtab("aprovados")}>Aprovados</button>
      </div>

      {/* ====== LISTA ====== */}
      {subtab === "pendentes" && (
        <section className="admin-sc-list">
          {pendLoading && pendentes.length === 0 && <div className="scm-spinner" aria-label="Carregando" />}
          {pendentes.map((it) => <LinhaSugestao key={it.id} it={it} contexto="pendente" />)}
          {pendentes.length === 0 && !pendLoading && <div className="admin-sc-empty">Nenhuma sugestão pendente.</div>}
          {pendentes.length > 0 && (
            <div className="scm-pager">
              <button type="button" className="scm-btn-more" onClick={() => loadPendentes(false)} disabled={pendLoading || !pendHasMore}>
                {pendLoading ? "Carregando…" : (pendHasMore ? "VER MAIS" : "FIM DA LISTA")}
              </button>
            </div>
          )}
        </section>
      )}

      {subtab === "votacao" && (
        <section className="admin-sc-list">
          {votaLoading && votacao.length === 0 && <div className="scm-spinner" aria-label="Carregando" />}
          {votacao.map((it) => <LinhaSugestao key={it.id} it={it} contexto="votacao" />)}
          {votacao.length === 0 && !votaLoading && <div className="admin-sc-empty">Nenhuma sugestão em votação.</div>}
          {votacao.length > 0 && (
            <div className="scm-pager">
              <button type="button" className="scm-btn-more" onClick={() => loadVotacao(false)} disabled={votaLoading || !votaHasMore}>
                {votaLoading ? "Carregando…" : (votaHasMore ? "VER MAIS" : "FIM DA LISTA")}
              </button>
            </div>
          )}
        </section>
      )}

      {subtab === "aprovados" && (
        <section className="admin-sc-list">
          {aprovLoading && aprovados.length === 0 && <div className="scm-spinner" aria-label="Carregando" />}
          {aprovados.map((it) => <LinhaSugestao key={it.id} it={it} contexto="aprovado" />)}
          {aprovados.length === 0 && !aprovLoading && <div className="admin-sc-empty">Nenhuma sugestão aprovada.</div>}
          {aprovados.length > 0 && (
            <div className="scm-pager">
              <button type="button" className="scm-btn-more" onClick={() => loadAprovados(false)} disabled={aprovLoading || !aprovHasMore}>
                {aprovLoading ? "Carregando…" : (aprovHasMore ? "VER MAIS" : "FIM DA LISTA")}
              </button>
            </div>
          )}
        </section>
      )}

      {/* ====== PAINEL: EDITAR (inline) ====== */}
      {editItem && (
        <section className="admin-sc-config" style={{marginTop:14}}>
          <div className="line" style={{justifyContent:"space-between"}}>
            <div className="lbl">Editar sugestão</div>
            <button type="button" className="admin-sc-btn" onClick={() => setEditItem(null)}>Fechar</button>
          </div>
          <div className="line" style={{flexDirection:"column", alignItems:"stretch", gap:8}}>
            <label className="lbl">Título / Nome</label>
            <input
              value={editItem?.titulo || ""}
              onChange={(e)=>setEditItem((x)=>({...(x||{}), titulo:e.target.value}))}
              className="num" style={{width:"100%"}}
            />
            <label className="lbl">Filme</label>
            <input
              value={editItem?.filme || ""}
              onChange={(e)=>setEditItem((x)=>({...(x||{}), filme:e.target.value}))}
              className="num" style={{width:"100%"}}
            />
            <label className="lbl">País</label>
            <input
              value={editItem?.pais || ""}
              onChange={(e)=>setEditItem((x)=>({...(x||{}), pais:e.target.value}))}
              className="num" style={{width:"100%"}}
            />
            <label className="lbl">URL da imagem</label>
            <input
              value={editItem?.imagemUrl || ""}
              onChange={(e)=>setEditItem((x)=>({...(x||{}), imagemUrl:e.target.value}))}
              className="num" style={{width:"100%"}}
            />
            <div style={{display:"flex", justifyContent:"flex-end", gap:8, marginTop:8}}>
              <button type="button" className="admin-sc-btn" onClick={() => setEditItem(null)}>Cancelar</button>
              <button type="button" className="admin-sc-btn" onClick={salvarEdicao}>Salvar</button>
            </div>
          </div>
        </section>
      )}

      {/* ====== PAINEL: VOTOS NECESSÁRIOS (inline) ====== */}
      {votosItem && (
        <section className="admin-sc-config" style={{marginTop:14}}>
          <div className="line" style={{justifyContent:"space-between"}}>
            <div className="lbl">Votos necessários</div>
            <button type="button" className="admin-sc-btn" onClick={() => setVotosItem(null)}>Fechar</button>
          </div>
          <div className="line" style={{alignItems:"center", gap:12}}>
            <input
              type="number"
              min={1}
              value={votosVal}
              onChange={(e)=>setVotosVal(e.target.value)}
              className="num"
            />
            <button type="button" className="admin-sc-btn" onClick={salvarVotosNec}>Salvar</button>
          </div>
        </section>
      )}
    </div>
  );
}
